import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Mail, Loader2, Paperclip, CheckCircle2, LinkIcon } from "lucide-react";

export default function EmailQuoteDialog({ open, onOpenChange, quote, defaultTo }) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [includeAccept, setIncludeAccept] = useState(true);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ connected: false, email: null });
  const [checking, setChecking] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const checkStatus = useCallback(async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/oauth/gmail/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false, email: null });
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (open && quote) {
      checkStatus();
      if (defaultTo) setTo(defaultTo);
      setSubject(`Freight Quote ${quote.quote_number} — Premier Handling Solutions`);
      setMessage(
        `Hello${quote.customer_name ? " " + quote.customer_name : ""},\n\n` +
          `Please find attached your freight quote ${quote.quote_number}. ` +
          `The all-in price is $${Number(quote.sell_price || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}.\n\n` +
          `Let us know if you have any questions — we're happy to help.\n\nThank you,\nPremier Handling Solutions · PHS Lift`,
      );
    }
  }, [open, quote, defaultTo, checkStatus]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/oauth/gmail/login");
      const popup = window.open(data.auth_url, "gmail-connect", "width=520,height=640");
      const onMsg = (e) => {
        if (e.data === "gmail-connected") {
          window.removeEventListener("message", onMsg);
          setTimeout(checkStatus, 400);
          setConnecting(false);
          toast.success("Google account connected.");
        }
      };
      window.addEventListener("message", onMsg);
      // Fallback: poll status if popup closed without postMessage
      const timer = setInterval(async () => {
        if (popup && popup.closed) {
          clearInterval(timer);
          window.removeEventListener("message", onMsg);
          await checkStatus();
          setConnecting(false);
        }
      }, 1000);
    } catch (e) {
      toast.error(apiError(e));
      setConnecting(false);
    }
  };

  const send = async () => {
    if (!quote) return;
    if (!to.trim()) return toast.error("Enter a recipient email.");
    setSending(true);
    try {
      await api.post(`/quotes/${quote.id}/email`, {
        to: to.trim(), subject, message, include_accept_link: includeAccept,
      });
      toast.success(`Quote emailed to ${to.trim()}.`);
      onOpenChange(false);
      setTo("");
    } catch (e) {
      if (e?.response?.status === 428) {
        toast.error("Connect your Google account first.");
        setStatus({ connected: false, email: null });
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="email-quote-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Mail className="h-5 w-5 text-[hsl(var(--primary))]" /> Email Quote {quote?.quote_number}
          </DialogTitle>
          <DialogDescription>Sent from your own @phsinc.com Gmail — replies come straight back to you.</DialogDescription>
        </DialogHeader>

        {checking ? (
          <div className="flex items-center gap-2 py-10 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking your Google connection…
          </div>
        ) : !status.connected ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="email-connect-prompt">
            <LinkIcon className="h-8 w-8 text-[hsl(var(--primary))]" />
            <p className="text-sm text-muted-foreground">
              Connect your Google account once to send quotes from your own inbox.
            </p>
            <Button data-testid="email-connect" onClick={connect} disabled={connecting} className="gap-2">
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Connect Google Account
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-md border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 px-3 py-2 text-xs text-[hsl(var(--success))]" data-testid="email-connected">
              <CheckCircle2 className="h-4 w-4" /> Connected as <span className="font-semibold">{status.email}</span>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">To</label>
              <Input data-testid="email-to" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@company.com" className="mt-1 h-9" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Subject</label>
              <Input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 h-9" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Message</label>
              <Textarea data-testid="email-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={7} className="mt-1" />
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/30 px-3 py-2 text-sm">
              <Paperclip className="h-4 w-4 text-muted-foreground" />
              <span>{quote?.quote_number}_customer.pdf will be attached</span>
            </div>
            <label className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
              <span>Include an "Accept this quote" link (marks quote Won when clicked)</span>
              <Switch data-testid="email-accept-toggle" checked={includeAccept} onCheckedChange={setIncludeAccept} />
            </label>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {status.connected && !checking && (
            <Button data-testid="email-send" onClick={send} disabled={sending} className="gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
